/** @jsxImportSource react */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import './Breadcrumbs.css';

export type BreadcrumbItem = { labelKey: string; path: string | null };

function getBreadcrumbItems(pathname: string): BreadcrumbItem[] {
  const base = { labelKey: 'title.panel', path: '/recruiter' as string };
  if (pathname === '/recruiter' || pathname === '/recruiter/') {
    return [{ labelKey: 'title.panel', path: null }];
  }
  if (pathname.startsWith('/recruiter/new')) {
    return [{ labelKey: 'title.new', path: null }];
  }
  if (pathname.startsWith('/recruiter/checkout')) {
    return [{ labelKey: 'title.checkout', path: null }];
  }
  if (pathname.startsWith('/recruiter/settings')) {
    return [{ labelKey: 'title.settings', path: null }];
  }
  const questionsMatch = pathname.match(/^\/recruiter\/vacancies\/([^/]+)\/questions\/?$/);
  if (questionsMatch) {
    return [base, { labelKey: 'title.questions', path: null }];
  }
  const workspaceMatch = pathname.match(/^\/recruiter\/workspace\/([^/]+)\/?$/);
  if (workspaceMatch) {
    return [base, { labelKey: 'title.candidatesList', path: null }];
  }
  if (pathname === '/recruiter/workspace' || pathname === '/recruiter/workspace/') {
    return [base, { labelKey: 'title.candidatesList', path: null }];
  }
  if (pathname.includes('/vacancies/') && pathname.includes('/edit')) {
    return [base, { labelKey: 'title.edit', path: null }];
  }
  if (pathname.startsWith('/recruiter/workspace')) {
    return [base, { labelKey: 'title.candidatesList', path: null }];
  }
  if (pathname.includes('/questions')) {
    return [base, { labelKey: 'title.questions', path: null }];
  }
  if (pathname.includes('/recruiter/metrics')) {
    return [base, { labelKey: 'title.metrics', path: null }];
  }
  return [base, { labelKey: 'title.panel', path: null }];
}

const iconProps = { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', className: 'breadcrumb-icon', width: 22, height: 22 };

/* Panel de vacantes (dashboard) */
const homeIconSvg = React.createElement(
  'svg',
  iconProps,
  React.createElement('rect', { x: '3', y: '3', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '14', y: '3', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '3', y: '14', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('rect', { x: '14', y: '14', width: '7', height: '7', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' })
);

/* Carga de Vacante (documento) */
const newVacancyIconSvg = React.createElement(
  'svg',
  iconProps,
  React.createElement('path', { d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' }),
  React.createElement('path', { d: 'M14 2V8H20', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' }),
  React.createElement('path', { d: 'M8 13H16M8 17H16', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round' })
);

/* Premium (estrella) */
const premiumIconSvg = React.createElement(
  'svg',
  iconProps,
  React.createElement('path', { d: 'M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z', stroke: 'currentColor', strokeWidth: '1.5', fill: 'none' })
);

/* Configuración (engrane) */
const settingsIconSvg = React.createElement(
  'svg',
  iconProps,
  React.createElement('circle', { cx: '12', cy: '12', r: '3', stroke: 'currentColor', strokeWidth: '2', fill: 'none' }),
  React.createElement('path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z', stroke: 'currentColor', strokeWidth: '2', fill: 'none' })
);

function getBreadcrumbIcon(labelKey: string): React.ReactNode {
  if (labelKey === 'title.panel') return homeIconSvg;
  if (labelKey === 'title.new') return newVacancyIconSvg;
  if (labelKey === 'title.checkout') return premiumIconSvg;
  if (labelKey === 'title.settings') return settingsIconSvg;
  return null;
}

/** Título estático de la vista de carga JD (CAPDT-308 — sin enlace al panel). */
export function RecruiterUploadPageTitle() {
  const { t } = useThemeLanguage();
  const icon = getBreadcrumbIcon('title.new');
  return React.createElement(
    'nav',
    {
      className: 'recruiter-breadcrumbs recruiter-upload-page-title',
      'aria-label': 'Breadcrumb',
      'data-testid': 'recruiter-upload-page-title',
    },
    React.createElement(
      'div',
      { className: 'recruiter-breadcrumbs-inner' },
      React.createElement(
        'span',
        { className: 'recruiter-breadcrumb-item recruiter-breadcrumb-item--current' },
        icon && React.createElement('span', { className: 'breadcrumb-icon-wrap' }, icon),
        React.createElement('span', null, t('title.new')),
      ),
    ),
  );
}

export function Breadcrumbs() {
  const { t } = useThemeLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const items = getBreadcrumbItems(location.pathname);

  if (items.length === 0) return null;
  if (items.length === 1 && items[0].path === null) {
    const icon = getBreadcrumbIcon(items[0].labelKey);
    return React.createElement(
      'nav',
      { className: 'recruiter-breadcrumbs', 'aria-label': 'Breadcrumb' },
      React.createElement(
        'div',
        { className: 'recruiter-breadcrumbs-inner' },
        React.createElement(
          'span',
          { className: 'recruiter-breadcrumb-item recruiter-breadcrumb-item--current' },
          icon && React.createElement('span', { className: 'breadcrumb-icon-wrap' }, icon),
          React.createElement('span', null, t(items[0].labelKey))
        )
      )
    );
  }

  return React.createElement(
    'nav',
    { className: 'recruiter-breadcrumbs', 'aria-label': 'Breadcrumb' },
    React.createElement(
      'div',
      { className: 'recruiter-breadcrumbs-inner' },
      items.map((item, index) => {
        const isFirst = index === 0;
        const isLink = item.path != null;
        const icon = isFirst ? getBreadcrumbIcon(item.labelKey) : null;
        return React.createElement(
          React.Fragment,
          { key: `${item.labelKey}-${index}` },
          index > 0 && React.createElement('span', { className: 'recruiter-breadcrumb-sep' }, '/'),
          isLink
            ? React.createElement(
              'button',
              {
                type: 'button',
                className: 'recruiter-breadcrumb-item recruiter-breadcrumb-item--link',
                onClick: () => navigate(item.path!),
              },
              icon && React.createElement('span', { className: 'breadcrumb-icon-wrap' }, icon),
              React.createElement('span', null, t(item.labelKey))
            )
            : React.createElement(
              'span',
              { className: 'recruiter-breadcrumb-item recruiter-breadcrumb-item--current' },
              icon && React.createElement('span', { className: 'breadcrumb-icon-wrap' }, icon),
              React.createElement('span', null, t(item.labelKey))
            )
        );
      })
    )
  );
}
